/**
 * Owner: Masa Bayu
 * File: spsu-structural-bridge.ts
 * Date:Time: 2026-10-05 14:55
 * Description: Fasa 20+23 · Universal pipeline → Classifier → Face layers + structural.
 */

import type {
  AssetLibrary,
  ImageElement,
  SPSUElement,
  SPSUPage,
  TextElement,
} from '../extract/spsu-types';
import {
  asEnrichedText,
  asImageElement,
  runUniversalPipeline,
} from './spsu-universal-pipeline-bridge';

export type FaceStructuralLayers = {
  readonly visual: readonly ImageElement[];
  readonly header: readonly TextElement[];
  readonly footer: readonly TextElement[];
  readonly body: readonly TextElement[];
  /** Table cells · absolute Face · never Knuth. */
  readonly table: readonly TextElement[];
  /** List items · absolute Face · never Knuth. */
  readonly list: readonly TextElement[];
};

function isDropcapLetter(el: TextElement): boolean {
  return /^\p{L}$/u.test(el.content.trim());
}

/**
 * Lapisan 2 → Face layers.
 * Table/list isolated before body · single-letter header reclaimed for dropcap.
 */
export function classifyPageToFaceLayers(
  page: SPSUPage,
  elements: readonly SPSUElement[],
  assets: AssetLibrary = {},
): FaceStructuralLayers {
  const byId = new Map(elements.map((el) => [el.id, el] as const));
  const { structured, universalById } = runUniversalPipeline(
    elements,
    assets,
    page.size.height,
  );

  const visual = structured.visualLayer
    .map((atom) => asImageElement(byId.get(atom.id)))
    .filter((el): el is ImageElement => el != null);

  const footer = structured.footerLayer
    .map((atom) => asEnrichedText(byId.get(atom.id), universalById))
    .filter((el): el is TextElement => el != null);

  const table = structured.tableLayer
    .map((atom) => asEnrichedText(byId.get(atom.id), universalById))
    .filter((el): el is TextElement => el != null);

  const list = structured.listLayer
    .map((atom) => asEnrichedText(byId.get(atom.id), universalById))
    .filter((el): el is TextElement => el != null);

  const headerRaw = structured.headerLayer
    .map((atom) => asEnrichedText(byId.get(atom.id), universalById))
    .filter((el): el is TextElement => el != null);

  const bodyRaw = structured.bodyLayer
    .map((atom) => asEnrichedText(byId.get(atom.id), universalById))
    .filter((el): el is TextElement => el != null);

  const header: TextElement[] = [];
  const body = [...bodyRaw];
  for (const el of headerRaw) {
    if (isDropcapLetter(el)) {
      body.push(el);
    } else {
      header.push(el);
    }
  }
  body.sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);

  return { visual, header, footer, body, table, list };
}
